import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { designTokens } from '../utils/designTokens';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <button
        onClick={onToggle}
        className="w-full py-4 px-6 text-left flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <span className="font-semibold text-gray-900 dark:text-white pr-8">{question}</span>
        <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="px-6 pb-4 text-gray-600 dark:text-gray-400">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqs = {
    general: [
      {
        id: 'free',
        question: 'Is Boop free to use?',
        answer: 'Yes! Creating an account, browsing prompts, creating your own prompts, rating, and all core community features are 100% free. We offer optional premium features (like AI-powered prompt improvement) that cost Boop Coins, our virtual currency.'
      },
      {
        id: 'coins',
        question: 'What are Boop Coins?',
        answer: 'Boop Coins (BC) are our virtual currency. You can purchase them in packs or earn them by contributing to the community. Use them for premium features or to buy marketplace content.'
      },
      {
        id: 'no-spending',
        question: 'Can I use Boop without spending money?',
        answer: 'Absolutely! The vast majority of Boop is free. You can earn Boop Coins through quality ratings, helpful comments, and achievements. Many users never spend a dime.'
      },
      {
        id: 'earn-coins',
        question: 'How do I earn free Boop Coins?',
        answer: 'Write detailed ratings (2 BC each), get helpful votes on comments (5 BC at 10 votes), make the weekly leaderboard (50 BC), earn badges (10-100 BC), and refer friends (25 BC). Daily earning cap is 20 BC from ratings/comments to prevent farming.'
      },
      {
        id: 'non-logged-in',
        question: 'Can I view prompts without signing up?',
        answer: 'You can view 1 full prompt without an account. After that, you\'ll see previews and need to create a free account to view more. This helps us build a community while remaining accessible.'
      },
      {
        id: 'always-free',
        question: 'Will Boop always keep content free?',
        answer: 'Core platform features and free content will ALWAYS be free. We\'re committed to knowledge accessibility. Marketplace and premium tools are optional extras for power users.'
      }
    ],
    selling: [
      {
        id: 'can-sell',
        question: 'Can I sell my prompts?',
        answer: 'Once you reach Level A (Advanced), have created 10+ free prompts, given 25+ ratings, and maintain a 7.0+ average rating, you can apply to sell content. This ensures sellers have proven quality.'
      },
      {
        id: 'why-threshold',
        question: 'Why can\'t I sell immediately?',
        answer: 'We require activity thresholds to prevent spam and ensure marketplace quality. Build your reputation with free content first, then unlock selling privileges. This protects buyers and maintains community trust.'
      },
      {
        id: 'pricing',
        question: 'What can I charge for my prompts?',
        answer: 'Marketplace items range from 10 BC (minimum) to 200 BC (maximum, about $10). We recommend 20-50 BC for most prompts. Price caps keep the platform accessible and prevent gouging.'
      },
      {
        id: 'earnings',
        question: 'How much do I earn from sales?',
        answer: 'You receive 82% of the sale price in Boop Coins (platform takes 18% fee). You can cash out coins at an 80% rate. Example: 100 BC sale → you get 82 BC → cash out for $3.28.'
      },
      {
        id: 'platform-fee',
        question: 'Why is the platform fee 18%?',
        answer: 'The fee covers payment processing, hosting, content moderation, and funds our creator rewards program for free contributors. It\'s higher than typical to keep Boop community-focused, not purely commercial.'
      },
      {
        id: 'cashout',
        question: 'How do I cash out my earnings?',
        answer: 'Once you have 500+ BC in earnings, request a payout via PayPal, Stripe, or bank transfer. Payouts process in 5-7 business days. You\'ll receive 80% conversion rate (e.g., 500 BC = $20).'
      },
      {
        id: 'refunds',
        question: 'Can I refund a marketplace purchase?',
        answer: 'Yes, 90-day refund window if content is defective or misrepresented. Report the issue and our team will review. Abusive refund requests may result in account suspension.'
      }
    ],
    premium: [
      {
        id: 'feature-costs',
        question: 'What do premium features cost?',
        answer: 'Prompt Improver (15 BC/use), Advanced Token Analysis (10 BC/use), Prompt Boost (50 BC/24hrs), Analytics Dashboard (20 BC/month), Advanced Search (5 BC/use or 30 BC/month unlimited).'
      },
      {
        id: 'subscription',
        question: 'Is there a subscription option?',
        answer: 'No, we intentionally avoid subscriptions. Pay only for features you actually use with Boop Coins. This keeps costs transparent and predictable.'
      },
      {
        id: 'premium-vs-free',
        question: 'Do premium features work better than free ones?',
        answer: 'Premium features are additional tools, not better versions. Free token counting is accurate; premium adds optimization suggestions. Free search works great; premium adds advanced filters.'
      },
      {
        id: 'try-premium',
        question: 'Can I try premium features before buying?',
        answer: 'Yes! New users get 50 free Boop Coins to try any premium feature. This lets you test before committing.'
      }
    ],
    safety: [
      {
        id: 'payment-security',
        question: 'Is my payment information secure?',
        answer: 'Yes. We use Stripe for payment processing and never store credit card details. All transactions are PCI-DSS compliant with industry-standard encryption.'
      },
      {
        id: 'report-content',
        question: 'Can I report inappropriate content?',
        answer: 'Yes! Use the report button on any prompt. Approved reports earn you 5 BC as a thank-you for protecting the community.'
      },
      {
        id: 'marketplace-reports',
        question: 'What happens to reported marketplace content?',
        answer: 'Marketplace content receives stricter moderation. Violations result in immediate removal and potential marketplace ban. Buyers receive automatic refunds.'
      },
      {
        id: 'data-privacy',
        question: 'What data do you collect?',
        answer: 'We collect account info, content you create, usage data, and payment info (processed by Stripe/PayPal). Your email and payment details are private. Prompts, ratings, and comments are public. See our Privacy Policy for full details.'
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white dark:bg-spooky-bg-darker">
      <Helmet>
        <title>FAQ - Boop</title>
        <meta name="description" content="Frequently asked questions about Boop, including monetization, selling prompts, premium features, and platform policies." />
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 dark:from-purple-900 dark:to-indigo-900 text-white">
        <div className={`max-w-7xl mx-auto px-${designTokens.spacing.pagePadding} py-12 text-center`}>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-purple-100">
            Everything you need to know about Boop
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className={`max-w-4xl mx-auto px-${designTokens.spacing.pagePadding} py-16`}>

        {/* General */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">General Questions</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            {faqs.general.map(faq => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openItems[faq.id]}
                onToggle={() => toggleItem(faq.id)}
              />
            ))}
          </div>
        </section>

        {/* Selling & Marketplace */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Selling & Marketplace</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            {faqs.selling.map(faq => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openItems[faq.id]}
                onToggle={() => toggleItem(faq.id)}
              />
            ))}
          </div>
        </section>

        {/* Premium Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Premium Features</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            {faqs.premium.map(faq => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openItems[faq.id]}
                onToggle={() => toggleItem(faq.id)}
              />
            ))}
          </div>
        </section>

        {/* Privacy & Safety */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Privacy & Safety</h2>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            {faqs.safety.map(faq => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openItems[faq.id]}
                onToggle={() => toggleItem(faq.id)}
              />
            ))}
          </div>
        </section>

        {/* Still have questions? */}
        <section className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We're here to help! Check out our documentation or contact support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/docs"
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              View Documentation
            </a>
            <a
              href="/contact"
              className="px-6 py-3 bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 border-2 border-purple-600 dark:border-purple-400 rounded-lg font-semibold hover:bg-purple-50 dark:hover:bg-gray-600 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FAQ;
